import { GravyBinder } from 'gravy-bind';
import { FarmhandMcw } from './farmhand-mcw';
import { QuoteProductLine } from './models/quoting/QuoteProductLine';
import { goToQuote } from './utils/go-to-quote';
import { onReady } from './utils/on-ready';

declare global {
    interface HTMLMdDialogElement extends HTMLDialogElement {
        showModal: () => void;
        close: () => void;
    }

    interface HTMLMdSwitchElement extends HTMLElement {
        checked: boolean;
    }

    var availableLines: any;
    var bundleableLines: any;
    var widthBreak: number;

    interface Window {
        mlq: MultilineQuote;
    }
}

export class MultilineQuote {
    productLinesTouched: boolean = false;
    selectLine: string = '';
    agentCode: string = '';
    subProducerAgentId: string = '';
    apAcids: string = '';
    selectedLine: QuoteProductLine;
    dialogBinder: GravyBinder;
    formBinder: GravyBinder;
    campaignCode: string = '';

    constructor() {
        const dialogEl = document.querySelector('.multiquote-bundle-dialog') as HTMLMdDialogElement | null;
        if (dialogEl) {
            this.dialogBinder = new GravyBinder(this, dialogEl);
        }

        // The form with class .multiquote-form
        const formEl = document.querySelector('.multiquote-form') as HTMLFormElement | null;
        if (formEl) {
            this.formBinder = new GravyBinder(this, formEl);
            this.formBinder.updateBindings();
        }

        onReady(() => this.initialize());
        const campaignCodeElement = document.getElementById('CampaignCode') as HTMLInputElement;
        this.campaignCode = campaignCodeElement?.value || '';
        const subProducerCodeElement = document.getElementById('SubProducerAgentId') as HTMLInputElement;
        this.subProducerAgentId = subProducerCodeElement?.value || '';
    }

    private initialize(): void {
        this.handleApSelection();
        this.selectDefaultLineByUrl();
    }

    private selectDefaultLineByUrl(): void {
        const path = window.location.pathname.toLowerCase();
        const routeMap: { base: string; id: string }[] = [
            { base: '/auto', id: '0' },
            { base: '/homeowners', id: '1' },
            { base: '/renters', id: '2' },
            { base: '/life', id: '5' },
            { base: '/business', id: '6' },
            { base: '/farm', id: '7' }
        ];
        const match = routeMap.find(r => path === r.base || path.startsWith(r.base + '/'));
        const defaultId: string | null = match ? match.id : null;
        if (defaultId !== null) {
            const input = document.getElementById('switch-input-' + defaultId) as HTMLInputElement | null;
            const mdSwitch = document.getElementById('switch-' + defaultId) as HTMLMdSwitchElement | null;
            if (input && mdSwitch) {
                input.checked = true;
                this.selectLine = defaultId;
                (mdSwitch as any).selected = true;
                mdSwitch.setAttribute('selected', '');
                mdSwitch.setAttribute('aria-checked', 'true');
                this.onProductLineChanged(defaultId, false);
                this.formBinder?.updateOutwardBindings();
            }
        }
    }

    private handleApSelection(): void {
        if (this.apAcids !== 'null') {
            const acidArray = this.apAcids.split(';');
            this.agentCode = acidArray.length > 1
                ? acidArray[Math.floor(Math.random() * acidArray.length)].replace(/"/g, '')
                : acidArray[0].replace(/"/g, '');
        }
    }

    canSubmit(): boolean {
        return this.getSelectedLines().length > 0;
    }

    getSelectedLines(): number[] {
        const singleSelect = (): number[] => {
            return this.selectLine ? [parseInt(this.selectLine, 10)] : [];
        };
        const switchIds = ['0', '1', '2', '3', '4', '5', '6', '7'];
        const selected: number[] = [];
        for (const id of switchIds) {
            const input = document.getElementById('switch-input-' + id) as HTMLInputElement | null;
            if (input && input.checked) {
                selected.push(parseInt(id, 10));
            }
        }
        return selected;
    }

    public onProductLineChanged(selectedLineId: string, fromContainerClick?: boolean): void {
        this.uncheckConflictingLines(selectedLineId);
        if (fromContainerClick) {
            const input = document.getElementById(`switch-input-${selectedLineId}`) as HTMLInputElement | null;
            const mdSwitch = document.getElementById(`switch-${selectedLineId}`) as HTMLMdSwitchElement | null;

            if (input) {
                input.checked = !input.checked;
                if (mdSwitch) { mdSwitch.checked = input.checked; }
            }
        }
        const input = document.getElementById(`switch-input-${selectedLineId}`) as HTMLInputElement | null;
        this.selectLine = input && input.checked ? selectedLineId : '';
        this.formBinder?.updateOutwardBindings();
    }

    private uncheckConflictingLines(selectedLineId: string): void {
        const autoSwitch = document.getElementById('switch-input-0') as HTMLInputElement | null;
        const rentersSwitch = document.getElementById('switch-input-1') as HTMLInputElement | null;
        const homeSwitch = document.getElementById('switch-input-2') as HTMLInputElement | null;
        const autoRentersSwitch = document.getElementById('switch-input-3') as HTMLInputElement | null;
        const autoHomeSwitch = document.getElementById('switch-input-4') as HTMLInputElement | null;
        const lifeSwitch = document.getElementById('switch-input-5') as HTMLInputElement | null;
        const businessSwitch = document.getElementById('switch-input-6') as HTMLInputElement | null;
        const farmSwitch = document.getElementById('switch-input-7') as HTMLInputElement | null;

        function uncheck(el: HTMLInputElement | null) {
            if (el) {
                el.checked = false;
                const sId = el.id.replace('switch-input-', 'switch-');
                const swEl = document.getElementById(sId) as HTMLMdSwitchElement | null;
                if (swEl) {
                    swEl.checked = false;
                }
            }
        }

        const lineName = this.getLineNameById(selectedLineId);

        switch (lineName) {
            case 'Home':
                uncheck(autoHomeSwitch);
                uncheck(autoRentersSwitch);
                break;
            case 'Renters':
                uncheck(autoHomeSwitch);
                uncheck(autoRentersSwitch);
                break;
            case 'Auto':
                uncheck(autoHomeSwitch);
                uncheck(autoRentersSwitch);
                break;

            case 'AutoRenters':
            case 'AutoHome':
                uncheck(autoSwitch);
                uncheck(homeSwitch);
                uncheck(rentersSwitch);
                if (lineName === 'AutoRenters') {
                    uncheck(autoHomeSwitch);
                } else {
                    uncheck(autoRentersSwitch);
                }
                break;

            case 'Life':
            case 'Business':
            case 'Farm and Crop':
                uncheck(autoSwitch);
                uncheck(homeSwitch);
                uncheck(rentersSwitch);
                uncheck(autoHomeSwitch);
                uncheck(autoRentersSwitch);
                break;

            default:
                break;
        }
    }

    private getLineNameById(id: string): string {
        switch (id) {
            case '0': return 'Auto';
            case '1': return 'Renters';
            case '2': return 'Home';
            case '3': return 'AutoRenters';
            case '4': return 'AutoHome';
            case '5': return 'Life';
            case '6': return 'Business';
            case '7': return 'Farm and Crop';
            default: return '';
        }
    }

    handleSubmit(event: Event): void {
        event.preventDefault();
        this.productLinesTouched = true;
        this.updateOutwardBindings();

        if (!this.canSubmit()) {
            return;
        }
        this.submit();
    }

    submit(): void {
        const selectedLines = this.getSelectedLines();

        if (selectedLines.length === 1 && bundleableLines.includes(selectedLines[0])) {
            this.showBundleDialog(selectedLines[0]);
        } else {
            this.goToQuotePage(selectedLines);
        }
    }

    showBundleDialog(product: number): void {
        this.selectedLine = product as QuoteProductLine;
        this.formBinder?.updateOutwardBindings();
        this.dialogBinder?.updateOutwardBindings();

        const dialogEl = document.querySelector('.multiquote-bundle-dialog') as HTMLMdDialogElement | null;
        if (!dialogEl) {
            return;
        }
        try {
            if (typeof dialogEl.showModal === 'function') {
                dialogEl.showModal();
            } else {
                (dialogEl as any).open = true;
            }
        } catch (e) {
            (dialogEl as any).open = true;
        }
    }

    goToQuotePage(businessLines: number[]): void {

        const progressEl = document.querySelector('.linear-progress') as HTMLElement;
        if (progressEl) {
            progressEl.style.display = 'grid';
        }

        let referralCode = new URLSearchParams(window.location.search).get('referralCode');
        let agentId = new URLSearchParams(window.location.search).get('agentId');

        if (this.campaignCode) {
            goToQuote(businessLines, this.agentCode, referralCode, agentId, this.subProducerAgentId, this.campaignCode);
        } else {
            goToQuote(businessLines, this.agentCode, referralCode, agentId, this.subProducerAgentId);
        }
    }

    private updateOutwardBindings(): void {
        this.formBinder?.updateOutwardBindings();
        this.dialogBinder?.updateOutwardBindings();
    }

    isUsingChips(): boolean {
        return Math.max(document.documentElement.clientWidth, window.innerWidth || 0) > widthBreak;
    }

    watchWindowSize(): void {
        window.addEventListener('resize', () => this.updateOutwardBindings());
    }
}

const mlq = new MultilineQuote();
window.mlq = mlq;
